class NotificationService {
  constructor() {
    this.permission = Notification.permission;
    this.isSupported = 'Notification' in window;
  }

  // Request permission for notifications
  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Browser notifications are not supported');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Check if notifications are enabled
  isEnabled() {
    return this.isSupported && this.permission === 'granted';
  }

  // Truncate message content if too long
  truncateMessage(message, maxLength = 80) {
    if (!message) return '';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength - 3) + '...';
  }

  // Get sender display name
  getSenderDisplayName(senderId, senderName, roomNames, currentUserId) {
    if (senderName) return senderName;
    if (roomNames && roomNames[senderId]) return roomNames[senderId];
    if (currentUserId && senderId === currentUserId) return 'You';
    return senderId === 'admin' ? 'Admin' : 'Unknown User';
  }

  // Show browser notification for new message
  showMessageNotification(messageData, roomData = {}, fallbackCallback = null) {
    if (!this.isEnabled()) {
      console.log('Notifications not enabled');
      // Call fallback if browser notifications aren't available
      if (fallbackCallback && typeof fallbackCallback === 'function') {
        fallbackCallback(messageData, roomData);
      }
      return null;
    }

    try {
      const { from, data, to, file, name, ext } = messageData;
      const { roomName, senderName, roomNames, currentUserId } = roomData;

      // Determine sender display name
      const displayName = this.getSenderDisplayName(from, senderName || roomNames?.[from], roomNames, currentUserId);
      
      // Determine room display name
      const displayRoomName = roomName || to || 'Chat Room';

      // Prepare message content
      let messageContent = '';
      if (file) {
        messageContent = `📷 ${name || 'Image'} ${ext ? `(${ext})` : ''}`;
      } else {
        messageContent = this.truncateMessage(data);
      }

      // Create notification title and body
      const title = `New message from ${displayName}`;
      const body = `Room: ${displayRoomName}\n${messageContent}`;

      // Notification options
      const options = {
        body: body,
        icon: '/favicon.ico', // You can customize this icon
        badge: '/favicon.ico',
        tag: `message-${to}-${from}`, // Prevents duplicate notifications
        requireInteraction: false,
        silent: false,
        data: {
          messageId: messageData.id,
          roomId: to,
          senderId: from,
          timestamp: messageData.sent_at || Date.now()
        }
      };

      // Create and show notification
      const notification = new Notification(title, options);

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        
        // Focus window if it exists
        if (window) {
          window.focus();
        }

        // Close notification
        notification.close();

        // Emit custom event for handling room switching
        const customEvent = new CustomEvent('notificationClick', {
          detail: {
            roomId: to,
            messageId: messageData.id,
            senderId: from
          }
        });
        window.dispatchEvent(customEvent);
      };

      // Handle notification errors
      notification.onerror = (error) => {
        console.error('Notification error:', error);
      };

      return notification;

    } catch (error) {
      console.error('Error showing notification:', error);
      // Call fallback if browser notification fails
      if (fallbackCallback && typeof fallbackCallback === 'function') {
        fallbackCallback(messageData, roomData);
      }
      return null;
    }
  }

  // Show notification with custom content
  showCustomNotification(title, message, options = {}) {
    if (!this.isEnabled()) {
      return null;
    }

    try {
      const defaultOptions = {
        body: message,
        icon: '/favicon.ico',
        requireInteraction: false,
        ...options
      };

      const notification = new Notification(title, defaultOptions);

      // Auto-close after specified time or 4 seconds
      setTimeout(() => {
        notification.close();
      }, options.autoClose || 4000);

      return notification;
    } catch (error) {
      console.error('Error showing custom notification:', error);
      return null;
    }
  }

  // Check if current tab is active/focused
  isTabActive() {
    return !document.hidden && document.hasFocus();
  }

  // Only show notification if tab is not active
  showNotificationIfTabInactive(messageData, roomData, fallbackCallback = null) {
    if (!this.isTabActive()) {
      return this.showMessageNotification(messageData, roomData, fallbackCallback);
    }
    return null;
  }

  // Initialize notification service
  async initialize() {
    if (!this.isSupported) {
      console.warn('Browser notifications not supported');
      return false;
    }

    // Check current permission status
    this.permission = Notification.permission;

    // If permission is default, don't auto-request
    // Let user manually enable from settings
    if (this.permission === 'default') {
      console.log('Notification permission not requested yet');
      return false;
    }

    return this.permission === 'granted';
  }

  // Get current permission status
  getPermissionStatus() {
    return {
      isSupported: this.isSupported,
      permission: this.permission,
      isEnabled: this.isEnabled()
    };
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;